'use client';

import { useState } from 'react';
import { Sparkles, Filter, Play, RefreshCw } from '@/components/ui/icons';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { RecommendationCard } from './recommendation-card';
import { useProductGuruAnalysis, useUpdateRecommendation } from '@/hooks/use-ai';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import type { Recommendation } from '@audra-flow/types';

interface ProductGuruPanelProps {
  projectId: string;
  onRunAnalysis?: () => void;
}

// Map AI service response to frontend Recommendation type
function mapRecommendation(rec: any): Recommendation {
  // Map AI service types to frontend types
  const typeMapping: Record<string, Recommendation['type']> = {
    'gap': 'data-gap',
    'gap_analysis': 'data-gap',
    'improvement': 'suggested-enhancement',
    'warning': 'conflicting-requirements',
    'insight': 'structure-improvement',
    'best_practice': 'suggested-enhancement',
    'risk_alert': 'conflicting-requirements',
    'consistency_check': 'conflicting-requirements',
  };

  // Map priority values
  const priorityMapping: Record<string, Recommendation['priority']> = {
    'critical': 'high',
    'high': 'high',
    'medium': 'medium',
    'low': 'low',
  };

  const mappedType = typeMapping[rec.type] || 'suggested-enhancement';
  const mappedPriority = priorityMapping[rec.priority] || priorityMapping[rec.impact] || 'medium';

  // Extract clean description - check multiple fields
  let description = '';
  
  // Priority order for description sources
  // Backend returns camelCase (suggestedAction), AI service returns snake_case (suggested_action)
  const suggestedAction = rec.suggestedAction?.action || rec.suggestedAction || rec.suggested_action
  const descSources = [
    typeof suggestedAction === 'string' ? suggestedAction : null,
    rec.description,
    rec.rationale,
  ];
  
  for (const source of descSources) {
    if (source && typeof source === 'string' && source.length > 20) {
      // Skip placeholder texts
      const lowerSource = source.toLowerCase();
      if (!lowerSource.includes('see full analysis') && 
          !lowerSource.includes('for details') &&
          source !== '[]' && 
          source !== '{}') {
        description = source;
        break;
      }
    }
  }
  
  // If still no description, create one from the title
  if (!description) {
    description = `AI has identified this as a ${mappedType.replace(/-/g, ' ')} item that requires attention.`;
  }

  // Get affects from various possible fields
  // Backend returns camelCase (affectedAreas), AI service returns snake_case (affected_areas)
  let affects: string[] = [];
  if (Array.isArray(rec.affectedAreas) && rec.affectedAreas.length > 0) {
    affects = rec.affectedAreas.filter((a: string) => a && a !== '[]');
  } else if (Array.isArray(rec.related_documents) && rec.related_documents.length > 0) {
    affects = rec.related_documents.filter((a: string) => a && a !== '[]');
  } else if (Array.isArray(rec.artifacts) && rec.artifacts.length > 0) {
    affects = rec.artifacts;
  } else if (Array.isArray(rec.affected_areas) && rec.affected_areas.length > 0) {
    affects = rec.affected_areas;
  } else if (rec.category && rec.category !== '[]') {
    affects = [rec.category];
  }

  return {
    id: rec.id,
    type: mappedType,
    priority: mappedPriority,
    title: rec.title || 'Recommendation',
    description: description,
    affects: affects,
    status: rec.status || 'PENDING',
  };
}

// Type options for filter
const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'missing-persona', label: 'Missing Persona' },
  { value: 'data-gap', label: 'Data Gap' },
  { value: 'conflicting-requirements', label: 'Conflicting Requirements' },
  { value: 'suggested-enhancement', label: 'Suggested Enhancement' },
  { value: 'structure-improvement', label: 'Structure Improvement' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'high', label: 'High Priority' },
  { value: 'medium', label: 'Medium Priority' },
  { value: 'low', label: 'Low Priority' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPLIED', label: 'Applied' },
  { value: 'DISMISSED', label: 'Dismissed' },
  { value: 'EDITED_AND_APPLIED', label: 'Edited & Applied' },
];

export function ProductGuruPanel({ projectId, onRunAnalysis }: ProductGuruPanelProps) {
  const [filterType, setFilterType] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const { data, isLoading, isError, refetch, isFetching } = useProductGuruAnalysis(projectId);
  const updateRecommendation = useUpdateRecommendation(projectId);
  const { toast } = useToast();

  const handleApply = async (id: string) => {
    try {
      await updateRecommendation.mutateAsync({ recommendationId: id, action: 'apply' });
      toast({
        title: "Recommendation Applied",
        description: "The recommendation has been marked as applied.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to apply recommendation.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = async (id: string) => {
    try {
      await updateRecommendation.mutateAsync({ recommendationId: id, action: 'edit' });
      toast({
        title: "Recommendation Edited",
        description: "The recommendation has been marked as edited and applied.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update recommendation.",
        variant: "destructive",
      });
    }
  };

  const handleDismiss = async (id: string) => {
    try {
      await updateRecommendation.mutateAsync({ recommendationId: id, action: 'dismiss' });
      toast({
        title: "Recommendation Dismissed",
        description: "The recommendation has been dismissed.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to dismiss recommendation.",
        variant: "destructive",
      });
    }
  };

  const handleOpenRelated = (id: string) => {
    const rec = recommendations.find((r) => r.id === id);
    if (rec?.affects && rec.affects.length > 0) {
      toast({
        title: "Related Areas",
        description: `Affects: ${rec.affects.join(', ')}`,
      });
    }
  };

  // Map recommendations from AI service response
  const recommendations: Recommendation[] = data?.recommendations?.map(mapRecommendation) || [];
  
  // Filter recommendations by type, priority, status
  const filteredRecommendations = recommendations
    .filter((r) => !filterType || r.type === filterType)
    .filter((r) => !filterPriority || r.priority === filterPriority)
    .filter((r) => !filterStatus || (r.status || 'PENDING') === filterStatus);

  // Stats from coverage data
  const coverage = data?.coverage;

  const handleRunAnalysis = () => {
    refetch();
    onRunAnalysis?.();
  };

  return (
    <div className="flex-1 bg-gray-50 overflow-hidden flex flex-col">
      {/* Header - matching Figma */}
      <div className="bg-white px-6 py-6">
        <div className="flex items-start justify-between">
          {/* Left side - Title and description (matching Figma) */}
          <div className="flex-1 min-w-0 mr-6">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-6 h-6 text-brand-primary shrink-0" />
              <h2 className="text-base font-semibold text-gray-900 tracking-tight">
                Product Guru
              </h2>
            </div>
            <p className="text-base text-gray-600 tracking-tight leading-6 whitespace-nowrap">
              AI analysis of your project artifacts. Review and apply improvements.
            </p>
            {coverage && (
              <div className="mt-3 flex gap-2 text-sm">
                <span className="text-gray-500">Coverage:</span>
                <span className="text-brand-primary">
                  Discovery {Math.round(coverage.discovery * 100)}%
                </span>
                <span className="text-brand-primary">
                  Definition {Math.round(coverage.definition * 100)}%
                </span>
              </div>
            )}
          </div>

          {/* Right side - Filters + Run Analysis (matching Figma) */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="h-[42px] px-4 flex items-center gap-2 bg-white border border-[#d1d5dc] rounded-[10px] hover:bg-gray-50 transition-colors text-gray-700"
            >
              <Filter className="w-4 h-4 text-[#374151]" />
              <span className="text-base text-[#374151] font-medium">Filters</span>
              {filtersExpanded ? (
                <ChevronUp className="w-4 h-4 text-[#6b7280]" />
              ) : (
                <ChevronDown className="w-4 h-4 text-[#6b7280]" />
              )}
            </button>
            <button
              onClick={handleRunAnalysis}
              disabled={isFetching}
              className="h-[42px] px-4 flex items-center gap-2 bg-gradient-to-r from-brand-primary to-[#9810fa] rounded-[10px] hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isFetching ? (
                <RefreshCw className="w-4 h-4 text-white animate-spin" />
              ) : (
                <Play className="w-4 h-4 text-white fill-white" />
              )}
              <span className="text-base text-white font-medium">
                {isFetching ? 'Analyzing...' : 'Run Analysis Now'}
              </span>
            </button>
          </div>
        </div>

        {/* Filter section - Type, Priority, Status (toggled by Filters button) */}
        {filtersExpanded && (
        <div className="mt-6 pt-6 border-t border-[#e5e7eb]">
          <div className="grid grid-cols-3 gap-6">
            {/* Type filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-900">Type</label>
              <div className="relative">
                <button
                  onClick={() => {
                    setTypeDropdownOpen(!typeDropdownOpen);
                    setPriorityDropdownOpen(false);
                    setStatusDropdownOpen(false);
                  }}
                  className="h-[42px] w-full px-4 flex items-center justify-between gap-2 bg-white border border-[#e5e7eb] rounded-[10px] hover:bg-gray-50/80 transition-colors text-left text-base text-gray-900"
                  aria-haspopup="listbox"
                  aria-expanded={typeDropdownOpen}
                  aria-label={`Filter by type: ${TYPE_OPTIONS.find((o) => o.value === filterType)?.label || 'All Types'}`}
                >
                  <span>{TYPE_OPTIONS.find((o) => o.value === filterType)?.label || 'All Types'}</span>
                  <ChevronDown className="w-4 h-4 text-[#6b7280] shrink-0" />
                </button>
                {typeDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setTypeDropdownOpen(false)} aria-hidden="true" />
                    <div className="absolute left-0 mt-2 min-w-[200px] bg-white border border-[#e5e7eb] rounded-[10px] shadow-lg py-1 z-20 overflow-hidden" role="listbox" aria-label="Recommendation type">
                      {TYPE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value || 'all'}
                          role="option"
                          aria-selected={filterType === opt.value}
                          onClick={() => {
                            setFilterType(opt.value);
                            setTypeDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-base ${
                            filterType === opt.value ? 'bg-[#f3e8ff] text-[#8200db] font-medium' : 'text-[#111827] hover:bg-gray-50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Priority filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-900">Priority</label>
              <div className="relative">
                <button
                  onClick={() => {
                    setPriorityDropdownOpen(!priorityDropdownOpen);
                    setTypeDropdownOpen(false);
                    setStatusDropdownOpen(false);
                  }}
                  className="h-[42px] w-full px-4 flex items-center justify-between gap-2 bg-white border border-[#e5e7eb] rounded-[10px] hover:bg-gray-50/80 transition-colors text-left text-base text-gray-900"
                  aria-haspopup="listbox"
                  aria-expanded={priorityDropdownOpen}
                  aria-label={`Filter by priority: ${PRIORITY_OPTIONS.find((o) => o.value === filterPriority)?.label || 'All Priorities'}`}
                >
                  <span>{PRIORITY_OPTIONS.find((o) => o.value === filterPriority)?.label || 'All Priorities'}</span>
                  <ChevronDown className="w-4 h-4 text-[#6b7280] shrink-0" />
                </button>
                {priorityDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setPriorityDropdownOpen(false)} aria-hidden="true" />
                    <div className="absolute left-0 mt-2 min-w-[180px] bg-white border border-[#e5e7eb] rounded-[10px] shadow-lg py-1 z-20 overflow-hidden" role="listbox" aria-label="Recommendation priority">
                      {PRIORITY_OPTIONS.map((opt) => (
                        <button
                          key={opt.value || 'all'}
                          role="option"
                          aria-selected={filterPriority === opt.value}
                          onClick={() => {
                            setFilterPriority(opt.value);
                            setPriorityDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-base ${
                            filterPriority === opt.value ? 'bg-[#f3e8ff] text-[#8200db] font-medium' : 'text-[#111827] hover:bg-gray-50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Status filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-900">Status</label>
              <div className="relative">
                <button
                  onClick={() => {
                    setStatusDropdownOpen(!statusDropdownOpen);
                    setTypeDropdownOpen(false);
                    setPriorityDropdownOpen(false);
                  }}
                  className="h-[42px] w-full px-4 flex items-center justify-between gap-2 bg-white border border-[#e5e7eb] rounded-[10px] hover:bg-gray-50/80 transition-colors text-left text-base text-gray-900"
                  aria-haspopup="listbox"
                  aria-expanded={statusDropdownOpen}
                  aria-label={`Filter by status: ${STATUS_OPTIONS.find((o) => o.value === filterStatus)?.label || 'All Status'}`}
                >
                  <span>{STATUS_OPTIONS.find((o) => o.value === filterStatus)?.label || 'All Status'}</span>
                  <ChevronDown className="w-4 h-4 text-[#6b7280] shrink-0" />
                </button>
                {statusDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setStatusDropdownOpen(false)} aria-hidden="true" />
                    <div className="absolute left-0 mt-2 min-w-[180px] bg-white border border-[#e5e7eb] rounded-[10px] shadow-lg py-1 z-20 overflow-hidden" role="listbox" aria-label="Recommendation status">
                      {STATUS_OPTIONS.map((opt) => (
                        <button
                          key={opt.value || 'all'}
                          role="option"
                          aria-selected={filterStatus === opt.value}
                          onClick={() => {
                            setFilterStatus(opt.value);
                            setStatusDropdownOpen(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-base ${
                            filterStatus === opt.value ? 'bg-[#f3e8ff] text-[#8200db] font-medium' : 'text-[#111827] hover:bg-gray-50'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Recommendations List - scrollable area */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {isLoading ? (
          // Loading skeleton - grid layout with same container as RecommendationCard
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-[14px] p-5 shadow-sm flex flex-col min-h-[236px]"
              >
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Skeleton className="h-5 w-5 rounded-full shrink-0" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                  <Skeleton className="h-5 w-24 rounded-full" />
                </div>
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full mb-1" />
                <Skeleton className="h-4 w-full mb-1" />
                <Skeleton className="h-4 w-2/3 mb-4" />
                <div className="flex gap-2 pt-3 border-t border-gray-100 mt-auto">
                  <Skeleton className="h-8 w-16 rounded-lg" />
                  <Skeleton className="h-8 w-14 rounded-lg" />
                  <Skeleton className="h-8 w-20 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          // Error state - centered
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Analysis Unavailable</h3>
              <p className="text-gray-600 mb-4">
                Unable to fetch AI recommendations. The AI service may be unavailable.
              </p>
              <button
                onClick={() => refetch()}
                className="h-10 px-4 bg-brand-primary text-white rounded-[10px] hover:bg-brand-primary/90"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : filteredRecommendations.length === 0 ? (
          // Empty state - centered
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 mx-auto mb-4 bg-brand-primary/10 rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {(filterType || filterPriority || filterStatus)
                  ? 'No Matching Recommendations'
                  : data
                    ? 'No Issues Found'
                    : 'Ready to Analyze Your Project'}
              </h3>
              <p className="text-gray-600 mb-6">
                {(filterType || filterPriority || filterStatus)
                  ? 'Try adjusting your filters to see more recommendations.'
                  : data
                    ? 'Great news! No gaps or issues were found in your project documentation. You can run a new analysis anytime to check for updates.'
                    : 'Click "Run Analysis Now" to get AI-powered recommendations for improving your project artifacts, identifying gaps, and ensuring best practices.'}
              </p>
              {(filterType || filterPriority || filterStatus) ? (
                <button
                  onClick={() => {
                    setFilterType('');
                    setFilterPriority('');
                    setFilterStatus('');
                  }}
                  className="h-10 px-4 border border-gray-300 rounded-[10px] hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              ) : (
                <button
                  onClick={handleRunAnalysis}
                  disabled={isFetching}
                  className="h-10 px-6 flex items-center gap-2 mx-auto bg-gradient-to-r from-brand-primary to-[#9810fa] rounded-[10px] hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {isFetching ? (
                    <RefreshCw className="w-4 h-4 text-white animate-spin" />
                  ) : (
                    <Play className="w-4 h-4 text-white fill-white" />
                  )}
                  <span className="text-base text-white">
                    {isFetching ? 'Analyzing...' : data ? 'Run New Analysis' : 'Run Analysis Now'}
                  </span>
                </button>
              )}
            </div>
          </div>
        ) : (
          // Recommendations grid - 2 columns on xl, 1 column on smaller
          // items-start prevents cards from stretching to match row height
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            {filteredRecommendations.map((recommendation) => (
              <RecommendationCard
                key={recommendation.id}
                recommendation={recommendation}
                onApply={() => handleApply(recommendation.id)}
                onEdit={() => handleEdit(recommendation.id)}
                onDismiss={() => handleDismiss(recommendation.id)}
                onOpenRelated={() => handleOpenRelated(recommendation.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
